import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  HiArrowLeft,
  HiUserGroup,
  HiUserCircle,
  HiXMark,
} from "react-icons/hi2";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { userService } from "../../services/userService";
import { organisationService } from "../../services/organisationService";
import Header from "../../components/layout/Header";
import CoachCodeCard from "../../components/settings/CoachCodeCard";
import { db } from "../../firebase/config";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import {
  Organisation,
  OrganisationMember,
  OrganisationMemberPrivacySettings,
} from "../../types/organisation";

// Extended interface with membership details
interface OrganisationWithMembership extends Organisation {
  // User's membership details
  userRole: "owner" | "admin" | "coach" | "athlete";
  userJoinedAt: Date;
  userInvitedBy?: string;
  userStatus: "active" | "pending" | "suspended";

  // Owner/Coach display data
  ownerName?: string;
  ownerUsername?: string;
  ownerImageUrl?: string;
  ownerImageUrlNormal?: string;
  ownerImageUrlTiny?: string;
  invitedByName?: string;

  // Privacy settings
  privacySettings?: OrganisationMemberPrivacySettings;
}

const CoachesCornerSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState<
    OrganisationWithMembership[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadingCoachingIds, setLoadingCoachingIds] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [savingOrgIds, setSavingOrgIds] = useState<Set<string>>(new Set());
  const [disconnectingOrg, setDisconnectingOrg] =
    useState<OrganisationWithMembership | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [userGender, setUserGender] = useState<string | null>(null);
  const [coachingGroupIds, setCoachingGroupIds] = useState<string[]>([]);

  const fetchOrganisations = useCallback(
    async (orgIds: string[], userId: string) => {
      if (!orgIds || orgIds.length === 0) {
        return [];
      }

      try {
        const orgPromises = orgIds.map(async (orgId) => {
          try {
            // Fetch organization document
            const orgDocRef = doc(db, "organisations", orgId);
            const orgDoc = await getDoc(orgDocRef);

            if (!orgDoc.exists()) {
              console.warn(`Organization ${orgId} not found`);
              return null;
            }

            const orgData = orgDoc.data();

            // Fetch user's membership details
            const memberDocRef = doc(
              db,
              "organisations",
              orgId,
              "members",
              userId,
            );
            const memberDoc = await getDoc(memberDocRef);

            if (!memberDoc.exists()) {
              console.warn(
                `User ${userId} not found in organization ${orgId} members`,
              );
              return null;
            }

            const memberData = memberDoc.data() as OrganisationMember;

            // Fetch all members to get count
            const membersCollection = collection(
              db,
              "organisations",
              orgId,
              "members",
            );
            const membersSnapshot = await getDocs(membersCollection);
            const memberCount = membersSnapshot.size;

            // Extract privacy settings with defaults
            const privacySettings: OrganisationMemberPrivacySettings = {
              wellnessLogs: memberData.privacySettings?.wellnessLogs ?? true,
              trainingLogs: memberData.privacySettings?.trainingLogs ?? true,
              niggleLogs: memberData.privacySettings?.niggleLogs ?? true,
              performanceLogs:
                memberData.privacySettings?.performanceLogs ?? true,
              healthLogs: memberData.privacySettings?.healthLogs ?? true,
              noteLogs: memberData.privacySettings?.noteLogs ?? true,
              femaleCycleLogs:
                memberData.privacySettings?.femaleCycleLogs ?? true,
              recoveryLogs: memberData.privacySettings?.recoveryLogs ?? true,
              shareAllSessions:
                memberData.privacySettings?.shareAllSessions ?? true,
              updatedAt: memberData.privacySettings?.updatedAt,
            };

            // Fetch owner/coach profile data
            let ownerName = "Unknown";
            let ownerUsername: string | undefined;
            let ownerImageUrl: string | undefined;
            let ownerImageUrlNormal: string | undefined;
            let ownerImageUrlTiny: string | undefined;

            if (orgData.ownerId) {
              try {
                const { userData: ownerData } =
                  await userService.getUserProfile(orgData.ownerId);
                if (ownerData) {
                  ownerName =
                    `${ownerData.name || ""} ${
                      ownerData.surname || ""
                    }`.trim() ||
                    ownerData.username ||
                    "Unknown";
                  ownerUsername = ownerData.username;
                  ownerImageUrl = ownerData.imageUrl;
                  ownerImageUrlNormal = ownerData.imageUrlNormal;
                  ownerImageUrlTiny = ownerData.imageUrlTiny;
                }
              } catch (err) {
                console.warn(`Error fetching owner ${orgData.ownerId}:`, err);
              }
            }

            // Try to fetch inviter name
            let invitedByName: string | undefined;
            if (memberData.invitedBy) {
              try {
                const { userData: inviterData } =
                  await userService.getUserProfile(memberData.invitedBy);
                if (inviterData) {
                  invitedByName =
                    `${inviterData.name || ""} ${
                      inviterData.surname || ""
                    }`.trim() || inviterData.username;
                }
              } catch (err) {
                console.warn(
                  `Error fetching inviter ${memberData.invitedBy}:`,
                  err,
                );
              }
            }

            return {
              id: orgId,
              name: orgData.name || "Unknown Organization",
              description: orgData.description,
              ownerId: orgData.ownerId,
              createdAt: orgData.createdAt?.toDate?.() || new Date(),
              updatedAt: orgData.updatedAt?.toDate?.() || new Date(),
              memberCount,
              // User's membership details
              userRole: memberData.role || "athlete",
              userJoinedAt:
                memberData.joinedAt instanceof Date
                  ? memberData.joinedAt
                  : (memberData.joinedAt as any)?.toDate?.() || new Date(),
              userInvitedBy: memberData.invitedBy,
              userStatus: memberData.status || "active",
              // Owner/Coach display data
              ownerName,
              ownerUsername,
              ownerImageUrl,
              ownerImageUrlNormal,
              ownerImageUrlTiny,
              invitedByName,
              privacySettings,
            } as OrganisationWithMembership;
          } catch (error) {
            console.error(`Error fetching organization ${orgId}:`, error);
            return null;
          }
        });

        const results = await Promise.all(orgPromises);
        return results.filter(
          (org): org is OrganisationWithMembership => org !== null,
        );
      } catch (error) {
        console.error("Error fetching organizations:", error);
        return [];
      }
    },
    [],
  );
useEffect(() => {
  const fetchCoachingGroupIds = async () => {
    if (currentUser?.uid) {
      try {
        setLoadingCoachingIds(true);
        const ids = await organisationService.getCoachingGroupIds(currentUser.uid);
        console.log("ids we have received ", ids)
        setCoachingGroupIds(ids);
      } catch (error) {
        console.error("Error fetching coaching group IDs:", error);
      } finally {
        setLoadingCoachingIds(false);
      }
    } else {
      setLoadingCoachingIds(false);
    }
  };

  fetchCoachingGroupIds();
}, [currentUser?.uid]);

// Fetch user data and organizations
useEffect(() => {
  const fetchData = async () => {
    // Don't fetch until we have the coaching IDs
    if (loadingCoachingIds) {
      return;
    }

    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user profile
      const { userData } = await userService.getUserProfile(currentUser.uid);

      // Set user gender
      if (userData?.gender) {
        setUserGender(userData.gender);
      }

      // Fetch organizations
      if (coachingGroupIds.length > 0) {
        const orgs = await fetchOrganisations(
          coachingGroupIds,
          currentUser.uid,
        );
        // Sort by join date (most recent first)
        orgs.sort((a, b) => {
          const aTime = a.userJoinedAt ? a.userJoinedAt.getTime() : 0;
          const bTime = b.userJoinedAt ? b.userJoinedAt.getTime() : 0;
          return bTime - aTime;
        });
        setOrganisations(orgs);
      } else {
        setOrganisations([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch data",
      );
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [currentUser?.uid, fetchOrganisations, coachingGroupIds, loadingCoachingIds]);


  // Toggle card expansion
  const toggleCardExpansion = useCallback((orgId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
      }
      return newSet;
    });
  }, []);

  // Handle privacy toggle change with auto-save
  const handlePrivacyToggle = useCallback(
    async (
      orgId: string,
      field: keyof OrganisationMemberPrivacySettings,
      currentValue: boolean,
    ) => {
      if (!currentUser?.uid) return;

      // Optimistically update UI
      setOrganisations((prev) =>
        prev.map((org) =>
          org.id === orgId
            ? {
                ...org,
                privacySettings: {
                  ...org.privacySettings,
                  [field]: !currentValue,
                } as OrganisationMemberPrivacySettings,
              }
            : org,
        ),
      );

      // Save to backend
      try {
        setSavingOrgIds((prev) => new Set(prev).add(orgId));

        const org = organisations.find((o) => o.id === orgId);
        if (!org) return;

        const updatedSettings: OrganisationMemberPrivacySettings = {
          ...org.privacySettings,
          [field]: !currentValue,
        } as OrganisationMemberPrivacySettings;

        await organisationService.updateMemberPrivacySettings(
          orgId,
          currentUser.uid,
          updatedSettings,
        );
      } catch (error) {
        console.error("Error saving privacy settings:", error);
        // Revert optimistic update on error
        setOrganisations((prev) =>
          prev.map((org) =>
            org.id === orgId
              ? {
                  ...org,
                  privacySettings: {
                    ...org.privacySettings,
                    [field]: currentValue,
                  } as OrganisationMemberPrivacySettings,
                }
              : org,
          ),
        );
        alert("Failed to save privacy settings. Please try again.");
      } finally {
        setSavingOrgIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(orgId);
          return newSet;
        });
      }
    },
    [currentUser?.uid, organisations],
  );

  // Handle disconnecting from organization
  const handleDisconnect = useCallback(async () => {
    if (!disconnectingOrg || !currentUser?.uid) return;

    try {
      setIsDisconnecting(true);

      // Remove user from organization
      await organisationService.removeMember(
        disconnectingOrg.id,
        currentUser.uid,
      );

      // Update local state
      setOrganisations((prev) =>
        prev.filter((org) => org.id !== disconnectingOrg.id),
      );

      // Close modal
      setDisconnectingOrg(null);
    } catch (error) {
      console.error("Error disconnecting from organization:", error);
      alert("Failed to disconnect from coaching group. Please try again.");
    } finally {
      setIsDisconnecting(false);
    }
  }, [disconnectingOrg, currentUser?.uid]);

  if (loading) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <div className="card shadow-lg p-8 mt-8 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">
            Error Loading Organizations
          </h3>
          <p className="text-base-content/70 mb-6">{error}</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors mr-4"
            >
              <HiArrowLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content">
              Coaching Groups
            </h1>
          </div>
          <button
            onClick={() => setShowHelpDialog(true)}
            className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Coaching Groups Information"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <CoachCodeCard
          onRedeemed={() => {
            if (!currentUser?.uid) return;
            organisationService
              .getCoachingGroupIds(currentUser.uid)
              .then(setCoachingGroupIds)
              .catch((err) => console.error("Error refreshing coaching groups:", err));
          }}
        />

        {/* Info Box */}
        {organisations.length > 0 && (
          <div className="">
            <div className="flex items-start p-4">
              <div>
                <p className="text-sm">
                  Coaching groups allow coaches to manage and track athletes.
                  Being part of a coaching group gives your coach access to your
                  training data and allows them to provide personalised
                  guidance. You can manage which data your coach has access to
                  by adjusting your privacy settings.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Organizations Cards */}
        {organisations.length > 0 ? (
          <div className="space-y-6">
            {organisations.map((org) => {
              const isExpanded = expandedCards.has(org.id);
              const isSaving = savingOrgIds.has(org.id);

              return (
                <div
                  key={org.id}
                  className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4"
                >
                  {/* Card Header */}
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Group Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-semibold text-base-content mb-1">
                            {org.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <div className="flex-shrink-0 h-6 w-6">
                              {org.ownerImageUrlTiny || org.ownerImageUrl ? (
                                <img
                                  src={org.ownerImageUrlTiny || org.ownerImageUrl}
                                  alt={org.ownerName}
                                  className="h-6 w-6 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-6 w-6 rounded-full bg-base-300 flex items-center justify-center">
                                  <HiUserCircle className="h-6 w-6 text-base-content/50" />
                                </div>
                              )}
                            </div>
                            <span>Coach: {org.ownerName}</span>
                            {org.ownerUsername && (
                              <span className="text-base-content/50">
                                @{org.ownerUsername}
                              </span>
                            )}
                          </div>
                          {org.description && (
                            <p className="text-sm text-base-content/60 line-clamp-2">
                              {org.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Badges */}
                      <div className="flex flex-col gap-2 items-end">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/30 uppercase`}
                        >
                          {org.userStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Settings Section */}
                  <div className="">
                    <div className="pb-4 space-y-4">
                      {/* Privacy Toggles */}
                      <div className="space-y-3">
                        {/* Wellness Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Wellness{" "}
                                <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your sleep quality, mood, and readiness
                                scores with your coach
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.wellnessLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "wellnessLogs",
                                    org.privacySettings?.wellnessLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Female Cycle Logs - Only shown for female users */}
                        {userGender === "Female" && (
                          <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="text-sm font-semibold text-base-content mb-1">
                                  Female Cycle{" "}
                                  <span className="font-light">Logs</span>
                                </h4>
                                <p className="text-xs text-base-content/70">
                                  Share your menstrual cycle tracking and
                                  related metrics with your coach
                                </p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer ml-4">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={
                                    org.privacySettings?.femaleCycleLogs ?? true
                                  }
                                  onChange={() =>
                                    handlePrivacyToggle(
                                      org.id,
                                      "femaleCycleLogs",
                                      org.privacySettings?.femaleCycleLogs ??
                                        true,
                                    )
                                  }
                                  disabled={isSaving}
                                />
                                <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Training Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Training{" "}
                                <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Allow coach to view your training sessions,
                                duration, intensity, and metrics
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.trainingLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "trainingLogs",
                                    org.privacySettings?.trainingLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Session Visibility */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Session{" "}
                                <span className="font-light">Visibility</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Let your coach see all of your sessions. When
                                turned off, your coach can only see sessions they
                                assigned to you.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.shareAllSessions ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "shareAllSessions",
                                    org.privacySettings?.shareAllSessions ??
                                      true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Niggle Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Niggle <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your injury tracking, pain levels, and
                                affected areas
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.niggleLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "niggleLogs",
                                    org.privacySettings?.niggleLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Performance Logs */}
                        <div className="bborder border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Performance{" "}
                                <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your performance test results and
                                benchmarks
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.performanceLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "performanceLogs",
                                    org.privacySettings?.performanceLogs ??
                                      true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Health Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Health <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your health stats, vital signs, and
                                medical information
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.healthLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "healthLogs",
                                    org.privacySettings?.healthLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Note Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Note <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your personal notes, reflections, and
                                observations
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={org.privacySettings?.noteLogs ?? true}
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "noteLogs",
                                    org.privacySettings?.noteLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>

                        {/* Recovery Logs */}
                        <div className="border border-base-300 rounded-lg p-4 bg-base-300">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-base-content mb-1">
                                Recovery{" "}
                                <span className="font-light">Logs</span>
                              </h4>
                              <p className="text-xs text-base-content/70">
                                Share your recovery sessions, rest days, and
                                recovery metrics
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer ml-4">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={
                                  org.privacySettings?.recoveryLogs ?? true
                                }
                                onChange={() =>
                                  handlePrivacyToggle(
                                    org.id,
                                    "recoveryLogs",
                                    org.privacySettings?.recoveryLogs ?? true,
                                  )
                                }
                                disabled={isSaving}
                              />
                              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Warning Note */}
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                          <span className="font-semibold">Note:</span> Disabling
                          certain data sharing may limit your coach's ability to
                          provide personalised guidance and feedback.
                        </p>
                      </div>

                      {/* Disconnect Button */}
                      <div className="pt-4 border-base-300">
                        <button
                          onClick={() => setDisconnectingOrg(org)}
                          className="w-full px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 bg-red-500/10 hover:bg-red-500/15 border border-red-500/30 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <HiXMark className="w-5 h-5" />
                          Leave Coaching Group
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Empty State
          <div className="mt-8 px-4">
            <h3 className="text-lg font-medium font-grotesk text-base-content mb-2">
              You're not part of any coaching groups yet
            </h3>
            <p className="text-sm text-base-content/60">
              Ask your coach to send you an invitation using your{" "}
              <strong>email</strong>, then enter the code from it above.
            </p>
          </div>
        )}
      </div>

      {/* Disconnect Confirmation Modal */}
      {disconnectingOrg && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-base-300">
              <h3 className="text-xl font-semibold text-base-content">
                Leave Coaching Group?
              </h3>
              <button
                onClick={() => setDisconnectingOrg(null)}
                disabled={isDisconnecting}
                className="text-base-content/50 hover:text-base-content/60 transition-colors disabled:opacity-50"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-4 bg-base-200 rounded-lg border border-base-300">
                <div className="flex-shrink-0 h-12 w-12">
                  {disconnectingOrg.ownerImageUrlNormal || disconnectingOrg.ownerImageUrl ? (
                    <img
                      src={disconnectingOrg.ownerImageUrlNormal || disconnectingOrg.ownerImageUrl}
                      alt={disconnectingOrg.ownerName}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-base-300 flex items-center justify-center">
                      <HiUserCircle className="h-8 w-8 text-base-content/50" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-base-content">
                    {disconnectingOrg.name}
                  </div>
                  <div className="text-xs text-base-content/60">
                    Coach: {disconnectingOrg.ownerName}
                  </div>
                </div>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-700 dark:text-red-400">
                  <span className="font-semibold">Warning:</span> You're about
                  to leave this coaching group. Your coach will no longer have
                  access to your training data, and you won't receive guidance
                  from them anymore.
                </p>
              </div>

              <p className="text-sm text-base-content/70">
                If you want to rejoin this group later, you'll need to ask your
                coach to send you a new invitation.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-base-300 bg-base-200">
              <button
                onClick={() => setDisconnectingOrg(null)}
                disabled={isDisconnecting}
                className="px-4 py-2 text-sm font-medium text-base-content/80 bg-base-100 border border-base-300 rounded-lg hover:bg-base-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isDisconnecting && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-base-100"></div>
                )}
                {isDisconnecting ? "Leaving..." : "Leave Group"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Dialog */}
      {showHelpDialog && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex backdrop-blur-sm items-center justify-center p-4">
          <div className="bg-base-100 w-full max-w-md rounded-2xl p-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold font-grotesk">
                Coaching Groups
              </h3>
              <button
                onClick={() => setShowHelpDialog(false)}
                className="text-base-content/60 hover:text-base-content/80 text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="space-y-4 text-base-content/70">
              <h3 className="font-grotesk text-lg font-medium">
                <strong>Coaching Groups</strong> connect you with your coach and
                allow them to monitor your training progress, provide feedback,
                and help you achieve your athletic goals.
              </h3>

              <p>
                When you join a coaching group, you can control exactly what
                data your coach can see through privacy settings. Each type of
                log (wellness, training, niggles, etc.) can be toggled on or off
                independently. Changes are saved automatically.
              </p>

              <p>
                <strong>Please Note:</strong> Disabling certain data types may
                limit your coach's ability to provide personalized guidance. We
                recommend sharing all relevant training data for the best
                coaching experience.
              </p>
              <p className="text-md font-grotesk font-semibold mb-2 text-blue-600">
                How to join a coaching group:
              </p>
              <p className="text-sm">
                Ask your coach to send you an invitation using your{" "}
                <strong>email</strong>. Once you receive it, you can accept and
                join their coaching group.
              </p>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-600">
                  <strong>Premium Subscription</strong> is required to connect
                  to a coach account.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowHelpDialog(false)}
              className="mt-6 w-full bg-neutral text-white font-grotesk py-3 rounded-lg font-bold hover:bg-neutral/90 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachesCornerSettings;
